import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ComparePage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      fetchTools(ids.split(','));
    } else {
      setLoading(false);
    }
  }, [searchParams]);

  const fetchTools = async (toolIds) => {
    try {
      const response = await axios.post(`${API}/compare`, {
        tool_ids: toolIds
      });
      setTools(response.data.tools || []);
    } catch (error) {
      console.error('Error fetching tools for comparison:', error);
      toast.error('Failed to load tools for comparison');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="loading-state">
          <p className="text-xl text-[#333333]">Loading comparison...</p>
        </div>
      </Layout>
    );
  }

  if (tools.length === 0) {
    return (
      <Layout>
        <div className="py-20 text-center" data-testid="no-tools">
          <p className="text-xl text-[#333333] mb-6">No tools to compare</p>
          <Button
            onClick={() => navigate('/')}
            className="bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-8 rounded-full font-semibold text-lg"
            data-testid="go-home-button"
          >
            Go Home
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-12 md:py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="mb-8">
            <button
              onClick={() => navigate(-1)}
              className="text-base text-[#0044CC] hover:underline focus-visible-ring rounded"
              data-testid="back-button"
            >
              ← Back to results
            </button>
          </div>

          <h1 className="text-4xl md:text-5xl font-semibold text-black mb-12 text-center" data-testid="compare-heading">
            Compare AI Tools
          </h1>

          <div className="overflow-x-auto">
            <table className="w-full border-2 border-gray-100 rounded-2xl overflow-hidden">
              <thead>
                <tr className="bg-gray-50">
                  <th className="p-6 text-left text-lg font-semibold text-black border-b-2 border-gray-100">
                    Feature
                  </th>
                  {tools.map((tool, index) => (
                    <th
                      key={tool.id}
                      className="p-6 text-left text-lg font-semibold text-black border-b-2 border-gray-100"
                      data-testid={`tool-header-${index}`}
                    >
                      <div>
                        <div className="text-xl mb-2">{tool.name}</div>
                        <span className="px-3 py-1 bg-white text-black text-sm font-medium rounded-full">
                          {tool.primary_category}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Description</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      {tool.description_plain}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Pricing</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      <div>{tool.pricing_model}</div>
                      {tool.starting_price && <div className="text-sm mt-1">{tool.starting_price}</div>}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Skill Level</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      {tool.skill_level}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Platforms</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      {tool.supported_platforms.join(', ')}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Enterprise Ready</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6">
                      {tool.enterprise_ready === 'Yes' ? (
                        <Check className="h-6 w-6 text-[#037F4C]" />
                      ) : (
                        <X className="h-6 w-6 text-[#D92D20]" />
                      )}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">API Available</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6">
                      {tool.api_available === 'Yes' ? (
                        <Check className="h-6 w-6 text-[#037F4C]" />
                      ) : (
                        <X className="h-6 w-6 text-[#D92D20]" />
                      )}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Agentic Capability</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6">
                      {tool.agentic_capability === 'Yes' ? (
                        <Check className="h-6 w-6 text-[#037F4C]" />
                      ) : (
                        <X className="h-6 w-6 text-[#D92D20]" />
                      )}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Privacy Level</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      {tool.privacy_level}
                    </td>
                  ))}
                </tr>

                <tr className="border-b border-gray-100">
                  <td className="p-6 font-semibold text-black bg-gray-50">Best For</td>
                  {tools.map((tool) => (
                    <td key={tool.id} className="p-6 text-[#333333]">
                      <ul className="space-y-1">
                        {tool.best_for.slice(0, 3).map((item, i) => (
                          <li key={i} className="text-sm">• {item}</li>
                        ))}
                      </ul>
                    </td>
                  ))}
                </tr>

                <tr>
                  <td className="p-6 font-semibold text-black bg-gray-50">Actions</td>
                  {tools.map((tool, index) => (
                    <td key={tool.id} className="p-6">
                      <div className="space-y-3">
                        <Button
                          onClick={() => navigate(`/tool/${tool.id}`)}
                          className="w-full bg-[#0044CC] text-white hover:bg-[#0044CC]/90 h-12 px-6 rounded-full font-semibold transition-colors"
                          data-testid={`view-details-${index}`}
                        >
                          View Details
                        </Button>
                        <Button
                          onClick={() => window.open(tool.official_website, '_blank')}
                          className="w-full bg-transparent border-2 border-[#0044CC] text-[#0044CC] hover:bg-[#0044CC]/5 h-12 px-6 rounded-full font-semibold transition-colors"
                          data-testid={`visit-website-${index}`}
                        >
                          Visit Website
                        </Button>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ComparePage;